<template>
  <div class="w-full h-full flex flex-col items-center">
    <div class="lg:text-center space-y-3 max-w-lg lg:max-w-xl">
      <BaseText type="display" size="2xs">
        {{ getGuestStatus?.isGuestOnly ? 'Your Guest Access is Activated' : 'Your Free Trial is Activated' }}
      </BaseText>
      <BaseText type="body" size="md" class="text-neutral-alpha-650">
        <span v-if="getGuestStatus?.isGuestOnly">
        Welcome to guest mode in Foreplay. You can view only the Lens you were invited to. For more access to all features, ask your admin for a team invite.
        </span>
        <span v-else>
          Here are some starting points to help you get started and make the most of your free trial, or continue to the Foreplay dashboard.
        </span>
      </BaseText>
    </div>
    <div class="w-full flex flex-col lg:flex-row items-center gap-4 justify-center mt-7 lg:mt-16" v-if="!getGuestStatus?.isGuestOnly">
      <div class="video-container w-full bg-neutral-alpha-100 rounded-xl">
        <WistiaPlayer 
          media-id="crfwokltfx"
          aspect="1.7777777777777777"
        />
      </div>
      <div class="flex-grow flex flex-col gap-2 justify-between" style="max-width: 520px;">
        <div v-for="suggestion in getStartedSuggestions" :key="suggestion.title"
        class="w-full flex flex-col sm:flex-row gap-4 p-4 rounded-3xl border border-neutral-alpha-100">
          <div class="flex-grow flex flex-col gap-1 min-w-0">
            <div class="flex items-center gap-1.5">
              <component :is="suggestion.icon" class="text-neutral-alpha-650 flex-shrink-0" />
              <BaseText type="label" size="md" class="text-neutral-alpha-800">
                {{ suggestion.title }}
              </BaseText>
            </div>
            <BaseText type="body" size="md" class="text-neutral-alpha-650">
              {{ suggestion.description }}
            </BaseText>
          </div>
          <div class="w-full sm:w-auto flex-shrink-0" :class="{'pl-5': !suggestion.loading}">
            <button class="w-full flex items-center justify-center gap-1.5 sm:w-auto py-2 px-3.5 rounded-lg transition-colors duration-100" 
            :class="suggestion.isCompleted 
              ? 'bg-neutral-alpha-25 text-neutral-alpha-300 cursor-default' 
              : 'text-neutral-alpha-800 bg-neutral-alpha-100 hover:bg-neutral-alpha-200'
            "
            @click="suggestion.action" :disabled="suggestion.isCompleted || suggestion.loading">
              <BaseLoadingSpinnerCircle v-if="suggestion.loading" :duration="1" />
              <CheckmarkIcon v-else-if="suggestion.isCompleted" />
              <BaseText type="label" size="md">
                {{ suggestion.isCompleted ? suggestion.ctaSuccess : suggestion.cta }}
              </BaseText>
            </button>
          </div>
        </div>
        <!-- Mobile App CTA (Desktop) -->
        <div class="hidden lg:flex w-full gap-4 p-4 rounded-3xl border border-neutral-alpha-100">
          <div class="flex-grow flex flex-col gap-1 min-w-0">
            <div class="flex items-center gap-1.5">
              <MobileIcon class="text-neutral-alpha-650" />
              <BaseText type="label" size="md" class="text-neutral-alpha-800">
                Download Mobile App
              </BaseText>
            </div>
            <BaseText type="body" size="md" class="text-neutral-alpha-650">
              Access your saved ads and boards anytime, anywhere with our app.
            </BaseText>
          </div>
          <!-- QR Code with hover zoom effect -->
          <div class="relative flex-shrink-0" style="width: 76px; height: 76px;"
          @mouseenter="qrCodeHovered = true" @mouseleave="qrCodeHovered = false">
            <img src="https://publicassets.foreplay.co/mobile-app-qr-code.png" alt="Mobile App QR Code" 
            class="w-full h-full transition-opacity duration-300" :class="{ 'opacity-25': qrCodeHovered }">
            <transition name="qr-code">
              <div v-show="qrCodeHovered" class="qr-popup absolute p-2 rounded-xl border border-neutral-alpha-100 bg-black z-20 pointer-events-none shadow-lg">
                <img src="https://publicassets.foreplay.co/mobile-app-qr-code.png" alt="Mobile App QR Code" class="w-full h-full">
              </div>
            </transition>
          </div>
        </div>
        <!-- Mobile App CTA (Mobile) -->
        <div class="lg:hidden w-full flex flex-col mt-4 pb-16">
          <BaseText type="body" size="md" class="text-neutral-alpha-650">
            The Foreplay web app is designed for desktop. To use our Ad Library on the go, download the mobile app:
          </BaseText>
          <div class="w-full grid grid-cols-1 xs:grid-cols-2 gap-x-4 gap-y-2 mt-6">
            <a class="w-full flex items-center justify-center gap-2 px-1 py-2 rounded-lg border border-neutral-alpha-100"
            href="https://apps.apple.com/ca/app/foreplay-ad-swipe-file/id6466097243" target="_blank">
              <img src="../../../assets/icons/apple-logo-white.svg" class="w-6 h-6">
              <BaseText type="label" size="md" class="text-white whitespace-nowrap">
                Download iOS
              </BaseText>
            </a>
            <a class="w-full flex items-center justify-center gap-2 px-1 py-2 rounded-lg border border-neutral-alpha-100"
            href="https://play.google.com/store/apps/details?id=co.foreplay.ForeplayMobile" target="_blank">
              <img src="../../../assets/icons/google-play-logo.svg" class="w-4 h-4">
              <BaseText type="label" size="md" class="text-white whitespace-nowrap">
                Download Android
              </BaseText>
            </a>
          </div>
        </div>
      </div>
    </div>
    <router-link :to="getGuestStatus?.isGuestOnly ? '/dashboard' : '/discovery'" class="hidden lg:block w-full p-2 rounded-lg text-white text-center mt-8 lg:mt-16 max-w-lg" 
    style="background-color: #1F69FF;">
      <BaseText type="label" size="md">
        Finish & Continue to Dashboard
      </BaseText>
    </router-link>
  </div>
</template>

<script>
import firebase from '@/api/config/FirebaseConfig'
import { mapGetters } from 'vuex'

// Icons
import ChromeIcon from '../../globals/Icons/OnboardIcons/ChromeIcon.vue'
import PhoneIcon from '../../globals/Icons/PhoneIcon.vue'
import MobileIcon from '../../globals/Icons/OnboardIcons/MobileIcon.vue'
import CheckmarkIcon from '../../globals/Icons/NewCheckmarkIcon.vue'
import WistiaPlayer from './WistiaPlayer.vue'

export default {
  name: 'FinishOnboardSection',
  components: {
    MobileIcon,
    CheckmarkIcon,
    WistiaPlayer,
  },
  data () {
    return {
      qrCodeHovered: false,
      chromeExtensionInstalled: false,
      extCheckInterval: null
    }
  },
  async mounted () {
    const currentUser = firebase.auth().currentUser

    // Check if the chrome runtime api is available
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      console.log('Chrome extension API not available')
      clearInterval(this.extCheckInterval)
      return
    }

    const createTokenFunction = firebase.functions().httpsCallable('createToken')
    const token = await createTokenFunction(currentUser.uid)

    // Init an interval to check if the chrome extension is installed
    this.extCheckInterval = setInterval(() => {
      try {
        chrome.runtime.sendMessage('eaancnanphggbfliooildilcnjocggjm', {
          event: 'auth',
          data: { token }
        }, (response) => {
          if (chrome.runtime.lastError) {
            console.log('Chrome extension not available:', chrome.runtime.lastError.message)
            return
          }
          if (response?.status === 'worked') {
            this.chromeExtensionInstalled = true
            clearInterval(this.extCheckInterval)
          }
        })
      } catch (error) {
        console.error('Failed to check chrome extension:', error)
        clearInterval(this.extCheckInterval)
      }
    }, 2000)
  },
  beforeDestroy () {
    clearInterval(this.extCheckInterval)
  },
  computed: {
    ...mapGetters('LensModule', ['getProviders']),
    ...mapGetters('AuthModule', ['getGuestStatus']),
    getStartedSuggestions () {
      return [
        {
          icon: ChromeIcon,
          title: 'Download Chrome Extension',
          description: 'Quicky save and organize ads while browsing with our Chrome extension.',
          action: () => { window.open('https://chromewebstore.google.com/detail/ad-library-save-ads-faceb/eaancnanphggbfliooildilcnjocggjm', '_blank') },
          cta: 'Download',
          ctaSuccess: 'Downloaded',
          isCompleted: this.chromeExtensionInstalled
        },
        {
          icon: PhoneIcon,
          title: 'Book a Product Demo',
          description: 'Schedule a walkthrough of the Foreplay platform and how to get the most out of it.',
          action: () => { window.open('https://www.foreplay.co/book-demo', '_blank') },
          cta: 'Book Demo',
        }
      ]
    },
  }
}
</script>

<style scoped>
.video-container {
  /* aspect-ratio: 26 / 17; */
  aspect-ratio: 1.7777777777777777;
  max-width: 520px;
  overflow: hidden;
}

.qr-popup {
  left: -240px;
  right: 88px;
  top: -76px;
  bottom: -76px;
}
.qr-code-enter-active, .qr-code-leave-active {
  transition: left 0.3s ease-in-out, top 0.3s ease-in-out, right 0.3s ease-in-out, bottom 0.3s ease-in-out;
}
.qr-code-enter, .qr-code-enter-from, .qr-code-leave-to {
  left: -10px;
  top: -10px;
  right: -10px;
  bottom: -10px;
}
.qr-code-enter-to, .qr-code-leave {
  left: -240px;
  right: 88px;
  top: -76px;
  bottom: -76px;
}
</style>